'use client'

import { useState } from "react"
import { Building2, Calendar, DollarSign, MessageCircle, CheckCircle2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { toast } from "sonner"

export default function SubmitToRentPage() {
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    location: '',
    monthly_budget: '',
    bedrooms: '',
    bathrooms: '',
    min_floor_area: '',
    parking: '',
    move_in_date: '',
    lease_term: '',
    notes: ''
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          inquiry_type: 'rent',
          message: `RENTAL REQUIREMENTS:\n\nProperty Type: ${formData.property_type}\nLocation: ${formData.location}\nMonthly Budget: ₱${formData.monthly_budget}\nBedrooms: ${formData.bedrooms}\nBathrooms: ${formData.bathrooms}\nFloor Area: ${formData.min_floor_area} sqm\nParking: ${formData.parking}\nMove-in Date: ${formData.move_in_date}\nLease Term: ${formData.lease_term}\n\nAdditional Notes:\n${formData.notes}`
        })
      })

      if (response.ok) {
        setSubmitted(true)
        toast.success('Requirements submitted successfully!')
      } else {
        toast.error('Failed to submit requirements. Please try again.')
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Requirements Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for submitting your rental requirements. Our team will review and contact you within 24 hours with matching properties.
          </p>
          <Button
            onClick={() => window.location.href = '/properties?listing_type=rent'}
            className="bg-[#d60500] hover:bg-[#b50400] text-white"
          >
            Browse Rentals
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-16 md:py-24 mt-16 md:mt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4">
            Submit Your Rental Requirements
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tell us what you're looking for and we'll find the perfect rental property for you
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-2 border-gray-200">
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#d60500]" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Juan Dela Cruz"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="juan@example.com"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+63 917 123 4567"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                </div>
              </div>

              {/* Property Requirements */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#d60500]" />
                  Rental Requirements
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="property_type"
                      value={formData.property_type}
                      onChange={(e: any) => handleChange(e)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#d60500] focus:ring-[#d60500]"
                    >
                      <option value="">Select Property Type</option>
                      <option value="condo">Condominium</option>
                      <option value="house">House</option>
                      <option value="apartment">Apartment</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="studio">Studio</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Location <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="e.g., BGC, Makati, Rockwell, Ortigas"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Budget (₱) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="monthly_budget"
                      type="number"
                      value={formData.monthly_budget}
                      onChange={handleChange}
                      required
                      placeholder="50000"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bedrooms
                    </label>
                    <Input
                      name="bedrooms"
                      type="number"
                      value={formData.bedrooms}
                      onChange={handleChange}
                      placeholder="2"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bathrooms
                    </label>
                    <Input
                      name="bathrooms"
                      type="number"
                      value={formData.bathrooms}
                      onChange={handleChange}
                      placeholder="2"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Floor Area (sqm)
                    </label>
                    <Input
                      name="min_floor_area"
                      type="number"
                      value={formData.min_floor_area}
                      onChange={handleChange}
                      placeholder="50"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parking Slots
                    </label>
                    <Input
                      name="parking"
                      type="number"
                      value={formData.parking}
                      onChange={handleChange}
                      placeholder="1"
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Move-in Date
                    </label>
                    <Input
                      name="move_in_date"
                      type="date"
                      value={formData.move_in_date}
                      onChange={handleChange}
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lease Term
                    </label>
                    <select
                      name="lease_term"
                      value={formData.lease_term}
                      onChange={(e: any) => handleChange(e)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#d60500] focus:ring-[#d60500]"
                    >
                      <option value="">Select Lease Term</option>
                      <option value="6 months">6 Months</option>
                      <option value="1 year">1 Year</option>
                      <option value="2 years">2 Years</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Requirements or Notes
                    </label>
                    <Textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Any specific requirements, preferred amenities, pets, furniture needs, etc."
                      className="border-gray-300 focus:border-[#d60500] focus:ring-[#d60500]"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t border-gray-200">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#d60500] hover:bg-[#b50400] text-white font-bold text-lg py-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Rental Requirements'
                  )}
                </Button>
                <p className="text-sm text-gray-500 text-center mt-3">
                  By submitting, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
